/**
 * Shared minimap setup for the React Flow canvases.
 *
 * The minimap drew nothing at all — a flat rectangle with no sign of the graph
 * in it. It skips any node it cannot size (`nodeHasDimensions`), and it sizes a
 * node from the object *we* pass in the `nodes` prop, not from React Flow's
 * measured copy. Neither canvas fed `dimensions` changes back into its nodes
 * (one kept only `position` changes, the other is a plain `useMemo`), so every
 * node looked unmeasured and the minimap rendered an empty mask.
 *
 * `withNodeSizes` stamps `initialWidth` / `initialHeight` on each node, which
 * satisfies that check without forcing the DOM size the way `width` / `height`
 * would: the canvas still measures and lays out from the real element. The
 * numbers only decide how big a node's rectangle looks in the overview, so the
 * card's Tailwind width and a representative height are enough.
 */

/** Colours. `nodeColor` lands on an inline `fill`, so a token that some theme
 * leaves undefined would make the declaration invalid and paint the rect solid
 * black. Only tokens every theme defines belong here — `--muted` and `--border`
 * are, `--foreground` and `--surface` are not — and each keeps a literal
 * fallback so a missing one can never paint a black box. */
export const MINIMAP = {
  // A group band (flowStyle.ts) is a faint wash in the overview, as it is on
  // the canvas; drawn solid it buried the cards on it under one grey slab.
  nodeColor: (node: { type?: string }) => (node.type === 'band' ? 'color-mix(in srgb, var(--muted, #8b949e) 20%, transparent)' : 'var(--muted, #8b949e)'),
  nodeStrokeColor: (node: { type?: string }) => (node.type === 'band' ? 'transparent' : 'var(--border, rgba(127, 127, 127, 0.6))'),
  nodeStrokeWidth: 2,
  nodeBorderRadius: 4,
  // The part outside the viewport, dimmed towards the page rather than to
  // black: a black veil turned the whole overview grey in the light theme.
  maskColor: 'color-mix(in srgb, var(--background, #14181a) 60%, transparent)',
  bgColor: 'var(--surface, var(--background, #14181a))',
} as const;

export interface NodeSize {
  width: number;
  height: number;
}

/** Node `type` → the size its rectangle gets in the overview. */
export type NodeSizes = Record<string, NodeSize>;

export function withNodeSizes<T extends { type?: string; style?: { width?: unknown; height?: unknown } }>(
  nodes: readonly T[],
  sizes: NodeSizes,
  fallback: NodeSize = { width: 240, height: 80 },
): T[] {
  return nodes.map((node) => {
    // A node that already states its own size (the architecture map sizes its
    // layers to their contents) is taken at its word; then the per-type table;
    // then a sane default, because a node with no size is a node the minimap
    // will not draw at all.
    const styled = node.style;
    const own =
      typeof styled?.width === 'number' && typeof styled?.height === 'number' ? { width: styled.width, height: styled.height } : undefined;
    const size = own ?? (node.type ? sizes[node.type] : undefined) ?? fallback;
    return { ...node, initialWidth: size.width, initialHeight: size.height };
  });
}
